import * as THREE from 'three'
import { debugLog, debugWarn } from '../utils/debug'

/**
 * Fixes animation tracks for objects that have been split into multiple meshes (e.g. by material)
 * The GLTF loader might split "Cube" into "Cube_1" and "Cube_2" inside a "Cube" group.
 * Animations targeting "Cube" will fail because the Group doesn't have morph targets.
 * This function retargets the tracks to the children.
 */

/**
 * Merges animations that have been split by the exporter (e.g. Object Action vs Shape Key Action).
 * This fixes cases where "Cube Test" (Transforms) and "Cube.011Action" (Morphs) are separate clips.
 */
// Helper to completely uncache a clip from the mixer to prevent ghosting
function uncacheClip(mixer, clip)
{
  const root = mixer.getRoot();
  const action = mixer.existingAction(clip, root);
  if (action)
  {
    action.stop();
    mixer.stopAllAction(); // Optional: Be careful if you have other animations
    mixer.uncacheClip(clip);
    // mixer.uncacheAction(clip, root); // Usually uncacheClip handles it
  }
}

export function mergeSplitAnimations(animations, mixer)
{
  if (!animations) return

  const toRemove = new Set()

  // Helper to get target node name from a clip (checks first track)
  const getTargetNode = (clip) =>
  {
    if (!clip.tracks.length) return null
    const name = clip.tracks[ 0 ].name
    return name.substring(0, name.indexOf('.'))
  }

  // Helper: is this a likely auto-generated name? (Ended with Action, or .001 etc)
  // And is it morph-only?
  const isMorphCandidate = (clip) =>
  {
    // Check for morph tracks
    const hasMorph = clip.tracks.some(t => t.name.includes('morphTargetInfluences'))
    // Check for transform tracks
    const hasTransform = clip.tracks.some(t => !t.name.includes('morphTargetInfluences'))

    // We mostly want to merge "Morph Only" clips into "Transform" clips
    return hasMorph && !hasTransform
  }

  animations.forEach(morphClip =>
  {
    if (toRemove.has(morphClip)) return
    if (!isMorphCandidate(morphClip)) return

    const targetNode = getTargetNode(morphClip)
    if (!targetNode) return

    // Find a matching "Main" clip that targets the same node but has a "User" name
    // We look for a clip that HAS transforms, targets same node.
    const mainClip = animations.find(c =>
      c !== morphClip &&
      !toRemove.has(c) &&
      getTargetNode(c) === targetNode &&
      // Prefer clips that look like user names (not containing "Action" usually, but "Cube Test" is valid)
      // Heuristic: The main clip usually has transforms.
      c.tracks.some(t => !t.name.includes('morphTargetInfluences'))
    )

    if (mainClip)
    {
      debugLog(`[AnimationManager] Merging split animation: "${morphClip.name}" (Morphs) -> "${mainClip.name}" (Main)`)

      // Clone tracks from morph clip and add to main clip
      morphClip.tracks.forEach(t =>
      {
        mainClip.tracks.push(t.clone())
      })

      toRemove.add(morphClip)
    }
  })

  // Remove merged clips
  if (toRemove.size > 0)
  {
    debugLog(`[AnimationManager] Removing ${toRemove.size} merged animation clips.`)
    // We can't easily resize the array in place safely while iterating, 
    // but the caller usually passes the 'animations' array from useGLTF.
    // We need to splice it.
    for (let i = animations.length - 1; i >= 0; i--)
    {
      if (toRemove.has(animations[ i ]))
      {
        if (mixer) uncacheClip(mixer, animations[ i ])
        animations.splice(i, 1)
      }
    }
  }
}

/**
 * Fixes animation tracks for objects that have been split into multiple meshes (e.g. by material).
 * 
 * Problem: When GLTF loader encounters a mesh with multiple materials, it creates:
 *   - A Group named "OriginalMesh"
 *   - Child Meshes named "OriginalMesh_1", "OriginalMesh_2", etc.
 *   
 * Animation tracks targeting "OriginalMesh.morphTargetInfluences" fail because:
 *   - The Group doesn't have morphTargetInfluences (only Meshes do)
 *   - The morph targets are on the child Meshes
 *   
 * Solution: Retarget morph tracks to the child meshes that actually have morph targets.
 */
export function fixSplitMeshAnimations(scene, animations)
{
  if (!scene || !animations) return

  let fixCount = 0

  // Build a lookup of all objects with morph targets for efficient searching
  const morphTargetObjects = new Map()
  scene.traverse(obj =>
  {
    if (obj.morphTargetInfluences && obj.morphTargetInfluences.length > 0)
    {
      morphTargetObjects.set(obj.name, obj)
      debugLog(`[AnimationManager] Found morph target object: "${obj.name}" with ${obj.morphTargetInfluences.length} morph targets`,
        obj.morphTargetDictionary ? Object.keys(obj.morphTargetDictionary) : 'no dictionary')

      if (obj.morphTargetInfluences && obj.morphTargetInfluences.length)
      {
        obj.morphTargetInfluences.fill(0)
        debugLog(`[AnimationManager] Reset ${obj.morphTargetInfluences.length} morph influences to 0 for clean state.`)
      }

      // new material if it does not exist
      if (!obj.material)
      {
        obj.material = new THREE.MeshStandardMaterial({
          color: 0x000000,
          roughness: 1,
          metalness: 0,
          morphTargets: true,
          side: THREE.DoubleSide
        })
      }

      // Ensure material supports morph targets
      // This is often required for valid shader compilation with morphs
      const materials = Array.isArray(obj.material) ? obj.material : [ obj.material ]
      materials.forEach(mat =>
      {
        // User requested explicit check/set for morphTargets: true
        if (mat.morphTargets !== true)
        {
          mat.morphTargets = true
          mat.needsUpdate = true
          debugLog(`[AnimationManager] Enabled morphTargets=true for material "${mat.name}" (Object: "${obj.name}")`)
        }
      })
    }
  })

  if (morphTargetObjects.size === 0)
  {
    debugLog('[AnimationManager] No morph target objects found in scene.')
    return
  }

  animations.forEach(clip =>
  {
    const newTracks = []
    let clipModified = false

    debugLog(`[AnimationManager] Processing clip "${clip.name}" with ${clip.tracks.length} tracks.`)

    clip.tracks.forEach((track) =>
    {
      // Only process morph target tracks
      if (!track.name.includes('morphTargetInfluences'))
      {
        newTracks.push(track)
        return
      }

      // Parse track name: "NodeName.morphTargetInfluences" or "NodeName.morphTargetInfluences[index]"
      const dotIndex = track.name.indexOf('.')
      const nodeName = track.name.substring(0, dotIndex)
      const propertyPath = track.name.substring(dotIndex + 1) // "morphTargetInfluences" or "morphTargetInfluences[0]"

      debugLog(`[AnimationManager] Processing morph track: "${track.name}" (node: "${nodeName}", property: "${propertyPath}")`)

      // Check if the exact object exists and has morph targets
      const exactMatch = morphTargetObjects.get(nodeName)
      if (exactMatch)
      {
        debugLog(`[AnimationManager] Exact match found for "${nodeName}" - keeping original track.`)
        newTracks.push(track)
        return
      }

      // Look for the node in scene - it might be a Group containing split meshes
      const node = scene.getObjectByName(nodeName)

      // Collect potential targets:
      // 1. Children of the node (if node is a Group)
      // 2. Objects with names starting with nodeName_ or nodeName. (split mesh naming convention)
      const potentialTargets = []

      if (node)
      {
        // Traverse the node and its descendants for morph targets
        node.traverse(child =>
        {
          if (child.morphTargetInfluences && child.morphTargetInfluences.length > 0)
          {
            potentialTargets.push(child)
          }
        })

        if (potentialTargets.length > 0)
        {
          debugLog(`[AnimationManager] Node "${nodeName}" exists. Found ${potentialTargets.length} morph targets in descendants:`,
            potentialTargets.map(t => t.name))
        }
      }

      // Also search for split-mesh naming patterns (in case node doesn't exist or no children found)
      if (potentialTargets.length === 0)
      {
        const prefix1 = nodeName + '_'
        const prefix2 = nodeName + '.'

        morphTargetObjects.forEach((obj, name) =>
        {
          if (name.startsWith(prefix1) || name.startsWith(prefix2))
          {
            if (!potentialTargets.includes(obj))
            {
              potentialTargets.push(obj)
            }
          }
        })

        if (potentialTargets.length > 0)
        {
          debugLog(`[AnimationManager] Found ${potentialTargets.length} split-mesh targets by prefix matching:`,
            potentialTargets.map(t => t.name))
        }
      }

      // No targets found - keep original track and warn
      if (potentialTargets.length === 0)
      {
        debugWarn(`[AnimationManager] No morph targets found for track "${track.name}". ` +
          `Target "${nodeName}" does not exist or has no morphable children.`)
        newTracks.push(track)
        return
      }

      // Create retargeted tracks for each potential target
      debugLog(`[AnimationManager] Retargeting track "${track.name}" to ${potentialTargets.length} target(s).`)

      potentialTargets.forEach(target =>
      {
        // Preserve the property path (including any index notation like [0])
        const newTrackName = `${target.name}.${propertyPath}`
        const newTrack = track.clone()
        newTrack.name = newTrackName
        newTracks.push(newTrack)
        fixCount++

        debugLog(`[AnimationManager]   -> Created track: "${newTrackName}" for target "${target.name}"`)
      })

      clipModified = true
    })

    // Replace clip tracks with the new set
    if (clipModified)
    {
      clip.tracks = newTracks
      debugLog(`[AnimationManager] Updated clip "${clip.name}" with ${newTracks.length} tracks.`)
    }
  })

  if (fixCount > 0)
  {
    debugLog(`[AnimationManager] Applied ${fixCount} morph track fix(es) total.`)
  } else
  {
    debugLog('[AnimationManager] No morph track fixes needed.')
  }
}

/**
 * Fixes Transform animations for objects that have been split into a Group + Children (Mesh_1, Mesh_2).
 * If the animation targets the Group ("Plane.137"), but the Group is "locked" by React or physics,
 * we can bypass the lock by applying the animation to the *Children* instead.
 * 
 * This creates visual movement (children move inside the group) even if parent is static.
 */
export function fixSplitGroupTransforms(scene, animations)
{
  if (!scene || !animations) return
  let fixCount = 0
  const groupTransforms = new Map()
  // 1. Find all "Split Groups" (Groups that were meshes but split due to materials)
  // We identify them by checking if they are Groups and have children with matching name pattern
  scene.traverse(obj =>
  {
    if (!obj.isGroup) return

    // Check if children have split names (e.g. "Obj_1", "Obj_2")
    const children = obj.children
    if (children.length < 1) return;
    // Simple heuristic: If it has children and is named "X", do children have "X"? 
    // Or if it's the target of an animation.
    // Actually, let's just create a map of all Groups for quick lookup.
    groupTransforms.set(obj.name, obj)
  })
  animations.forEach(clip =>
  {
    const newTracks = []
    let clipModified = false
    clip.tracks.forEach(track =>
    {
      // Only process Transform tracks (position, quaternion, scale)
      if (track.name.includes('morphTargetInfluences'))
      {
        newTracks.push(track)
        return
      }
      // Parse track name
      const dotIndex = track.name.indexOf('.')
      const nodeName = track.name.substring(0, dotIndex)
      const propertyPath = track.name.substring(dotIndex + 1)

      const group = groupTransforms.get(nodeName)

      // Criteria: 
      // 1. Target is a Group in our scene.
      // 2. That Group has Mesh children.
      const meshChildren = group && group.children ? group.children.filter(c => c.isMesh) : []

      if (group && meshChildren.length > 0)
      {
        // debugLog(`[AnimationManager] Retargeting transform track "${track.name}" from Group "${nodeName}" to children.`)

        // For each child mesh, create a copy of the track
        meshChildren.forEach(child =>
        {
          const newTrackName = `${child.name}.${propertyPath}`
          const newTrack = track.clone()
          newTrack.name = newTrackName
          newTracks.push(newTrack)
          fixCount++

          if (propertyPath.includes('scale'))
          {
            debugLog(`[AnimationManager] Retargeting SCALE track from "${nodeName}" to "${newTrackName}"`)
          }
        })
        // Do NOT add the original track (effectively removing it from the Group)
        clipModified = true
      } else
      {
        newTracks.push(track)
      }
    })
    if (clipModified)
    {
      clip.tracks = newTracks
      debugLog(`[AnimationManager] Retargeted ${fixCount} tracks in clip "${clip.name}" from Groups to Children.`)
    }
  })
}


export class AnimationManager
{
  animations = []
  actions = {}
  loopingAnimations = []

  constructor(animations, actions)
  {
    this.animations = animations
    this.actions = actions
    this.loopingAnimations = []

    debugLog('[AnimationManager] Initialized with animations:', animations.map(a => a.name))
  }

  playAnimation(name, loopType = THREE.LoopOnce)
  {
    const action = this.actions[ name ]
    if (!action)
    {
      debugWarn(`[AnimationManager] Animation "${name}" not found!`)
      return null
    }

    if (action.isRunning())
    {
      debugLog(`[AnimationManager] Animation "${name}" is already running.`)
      return null
    }

    debugLog(`[AnimationManager] Playing animation: "${name}"`)

    // Debug: Inspect tracks for this animation
    if (action.getClip())
    {
      // Validation: Check bindings regardless of morph track presence to debug
      const mixer = action.getMixer()
      const root = mixer.getRoot()

      // Force update the mixer bindings to check status immediately
      mixer.update(0)

      debugLog(`[AnimationManager]   - Inspecting bindings on root object:`, root.name)

    }

    action.setLoop(loopType)
    action.clampWhenFinished = true
    action.reset()
    action.play()

    const duration = action._clip.duration * 1000
    return new Promise(resolve => setTimeout(resolve, duration))
  }

  playLoopingAnimations()
  {
    this.loopingAnimations.forEach(name => this.playAnimation(name, THREE.LoopRepeat))
  }

  getLoopingAnimations()
  {
    return this.loopingAnimations
  }

  getBoundedActions(object)
  {
    const boundActions = []
    Object.entries(this.actions).forEach(([ key, action ]) =>
    {
      const binding = action?._propertyBindings?.[ 0 ]?.binding
      if (binding?.node?.name === object.name) boundActions.push(action)
    })
    return boundActions
  }

  parseAnimations(object)
  {
    const userData = object.userData

    if ('LoopingAnimations' in userData)
    {
      let loopingAnims = this.normalizeAnimationList(userData.LoopingAnimations)

      // AUTO-FIX: Check if animations exist. If not, try to find a matching animation for this object.
      // This handles cases where Blender exports an Action with a different name than what's in Custom Properties (e.g. "Cube Test" vs "Cube.011Action")
      loopingAnims = loopingAnims.map(animName =>
      {
        if (this.actions[ animName ]) return animName // Exists, good.

        // Not found. Try to find an action that targets this object.
        debugWarn(`[AnimationManager] Animation "${animName}" requested by object "${object.name}" was not found. Attempting to find a match...`)

        const candidateEntry = Object.entries(this.actions).find(([ name, action ]) =>
        {
          const clip = action.getClip()
          if (!clip) return false

          // Check if any track in this clip targets the object
          return clip.tracks.some(track =>
          {
            // Track name format is "NodeName.property"
            // We check if it starts with "ObjectName."
            return track.name.startsWith(`${object.name}.`)
          })
        })

        if (candidateEntry)
        {
          const [ foundName ] = candidateEntry
          debugLog(`[AnimationManager] FIXED: Replaced missing animation "${animName}" with "${foundName}" for object "${object.name}"`)
          return foundName
        }

        return animName // Fallback
      })

      this.loopingAnimations.push(...loopingAnims)
      userData.LoopingAnimations = loopingAnims

      debugLog(`[AnimationManager] Found LoopingAnimations for ${object.name}:`, loopingAnims)
    }

    this.parseInteractionAnimations(object)
  }

  /** @private */
  parseInteractionAnimations(object)
  {
    const userData = object.userData
    const animationKeys = [ 'OnPointerEnterAnimations', 'OnPointerExitAnimations', 'OnSelectAnimations' ]

    let hasAnimations = false
    animationKeys.forEach(key =>
    {
      if (key in userData)
      {
        userData[ key ] = this.normalizeAnimationList(userData[ key ])
        hasAnimations = true
      }
    })

    if (hasAnimations)
    {
      if (!('zone' in userData)) userData.zone = '_default_animations_zone'
      userData.type = 'interactable'

      const userDataCopy = { ...userData }
      object.traverse(node => { node.userData = userDataCopy })
    }
  }

  /** @private */
  normalizeAnimationList(value)
  {
    if (Array.isArray(value)) return value
    if (typeof value === 'string')
    {
      return value.split(',').map(s => s.trim()).filter(Boolean)
    }
    return []
  }

  cloneAnimations(object, animation)
  {
    const animationClone = animation.clone()
    animationClone.setLoop(THREE.LoopOnce)
    animationClone.clampWhenFinished = true

    if (!object.userData.animationClones) object.userData.animationClones = []
    object.userData.animationClones.push(animationClone)
  }
}
